<template>
  <div :class="isOwnMessage ? 'chat-details receiver' : 'chat-details'">
    <img
      v-if="message.userImageURL && message.userImageURL !== ''"
      :src="getImgUrl(message.userImageURL)"
      alt="User avatar"
    />
    <TenantLogo v-else id="main-logo" logo-class="" />
    <div
      class="chat-wrapper"
      @mouseenter="showActions = true"
      @mouseleave="showActions = false"
    >
      <span class="chat-name-time-wrapper">
        <span class="chat-name">{{ message.name }}</span>
        <span class="chat-time">
          - {{ getTimeFromDate(message.createdDate) }}
          <span v-if="message.isEdited" class="edited-indicator">(edited)</span>
          <span
            v-if="isOwnMessage"
            class="read-receipt"
            :title="readReceiptText"
            :aria-label="readReceiptText"
          >
            <i
              class="bi bi-check2-all"
              :class="allOthersRead ? 'text-primary' : 'text-secondary'"
              style="font-size: 1.5em"
              aria-hidden="true"
            ></i>
            <span v-if="readCount > 0 && !allOthersRead" class="read-count">{{
              readCount
            }}</span>
          </span>
        </span>
      </span>

      <div class="chat-message-wrapper">
        <!-- Quoted message (reply) -->
        <div v-if="message.replyTo" class="reply-quote">
          <span class="reply-quote-name">{{ message.replyTo.name }}</span>
          <span class="reply-quote-preview">{{ message.replyTo.preview }}</span>
        </div>

        <!-- Poll -->
        <div v-if="isPoll" class="poll-message">
          <div class="poll-question">
            <i class="bi bi-bar-chart-fill"></i> {{ message.poll.question }}
          </div>
          <div class="poll-options">
            <button
              v-for="(opt, i) in message.poll.options"
              :key="i"
              type="button"
              class="poll-option"
              :class="{ voted: myPollVotes.includes(i) }"
              @click="votePoll(i)"
            >
              <span
                class="poll-option-fill"
                :style="{ width: pollPercent(i) + '%' }"
              ></span>
              <span class="poll-option-body">
                <span class="poll-option-check">
                  <i
                    :class="
                      myPollVotes.includes(i)
                        ? 'bi bi-check-circle-fill'
                        : 'bi bi-circle'
                    "
                  ></i>
                </span>
                <span class="poll-option-label">{{ opt }}</span>
                <span class="poll-option-count">{{ pollVoteCounts[i] }}</span>
              </span>
            </button>
          </div>
          <div class="poll-footer">
            {{ pollTotalVotes }} vote{{ pollTotalVotes === 1 ? "" : "s" }}
            <span v-if="message.poll.allowMultiple"> · Select one or more</span>
          </div>
        </div>

        <!-- Media Content -->
        <div v-if="message.media" class="message-media">
          <div
            v-if="getMediaType(message.media) === 'image'"
            class="media-image"
          >
            <img
              :src="getMediaUrl(message.media)"
              :alt="message.messageContent || 'Image'"
              @click="openMediaModal(message.media)"
              class="chat-image"
            />
          </div>

          <div
            v-else-if="getMediaType(message.media) === 'video'"
            class="media-video"
          >
            <video
              :src="getMediaUrl(message.media)"
              controls
              class="chat-video"
              preload="metadata"
            ></video>
          </div>

          <div
            v-else-if="getMediaType(message.media) === 'audio'"
            class="media-audio"
          >
            <audio
              :src="getMediaUrl(message.media)"
              controls
              class="chat-audio"
            ></audio>
          </div>

          <div v-else class="media-file">
            <div class="file-download">
              <div class="file-icon">
                {{ getFileIconFromMedia(message.media) }}
              </div>
              <div class="file-details">
                <span class="file-name">{{
                  getFileNameFromMedia(message.media)
                }}</span>
                <a
                  :href="getMediaUrl(message.media)"
                  download
                  class="download-link"
                >
                  Download
                </a>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="!isEditing && message.messageContent && !isPoll"
          class="chat-message"
          :class="{ 'squad-message': isSquadAnnouncement(message) }"
        >
          <div
            v-if="isSquadAnnouncement(message)"
            v-html="formatMessageContent(message)"
          ></div>
          <div v-else>{{ message.messageContent }}</div>
        </div>
        <div v-else-if="isEditing" class="edit-message-form">
          <textarea
            v-model="editText"
            class="edit-textarea"
            @keydown.enter.exact.prevent="saveEdit"
            @keyup.escape="cancelEdit"
            rows="2"
            ref="editTextarea"
          ></textarea>
          <div class="edit-actions">
            <button
              @click="saveEdit"
              class="save-btn"
              :disabled="!editText.trim()"
            >
              Save
            </button>
            <button @click="cancelEdit" class="cancel-btn">Cancel</button>
          </div>
        </div>

        <!-- Reaction chips -->
        <div v-if="reactionEntries.length" class="reaction-chips">
          <button
            v-for="[emoji, uids] in reactionEntries"
            :key="emoji"
            class="reaction-chip"
            :class="{ mine: uids.includes(String(currentUserId)) }"
            :title="uids.includes(String(currentUserId)) ? 'Remove reaction' : 'React'"
            @click="toggleReaction(emoji)"
          >
            {{ emoji }} <span class="reaction-count">{{ uids.length }}</span>
          </button>
        </div>

        <div v-if="showActions" class="message-actions">
          <div v-if="showPalette" class="reaction-palette">
            <button
              v-for="e in paletteEmojis"
              :key="e"
              class="palette-emoji"
              @click="pickReaction(e)"
            >
              {{ e }}
            </button>
          </div>
          <button
            @click="showPalette = !showPalette"
            class="action-btn react-btn"
            title="Add reaction"
            aria-label="Add reaction"
          >
            <i class="bi bi-emoji-smile"></i>
          </button>
          <button
            @click="replyToMessage"
            class="action-btn reply-btn"
            title="Reply"
            aria-label="Reply to message"
          >
            <i class="bi bi-reply"></i>
          </button>
          <button
            v-if="canEdit"
            @click="startEdit"
            class="action-btn edit-btn"
            title="Edit message"
          >
            <i class="bi bi-pencil"></i>
          </button>
          <button
            v-if="canDelete"
            @click="deleteMessage"
            class="action-btn delete-btn"
            title="Delete message"
          >
            <i class="bi bi-trash"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { processMessage, isSquadMessage } from "@/utils/messageFormatter";
import TenantLogo from "@/components/TenantLogo/index.vue";

export default {
  name: "ChatMessage",
  components: {
    TenantLogo,
  },
  props: {
    message: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    currentUserId: {
      type: [String, Number],
      required: true,
    },
    allowEditing: {
      type: Boolean,
      default: true,
    },
    userData: {
      type: Object,
      default: () => ({}),
    },
    // Member count of the chat group (incl. sender) — drives the
    // all-read blue tick. 0 = unknown, tick stays grey.
    groupSize: {
      type: Number,
      default: 0,
    },
  },
  emits: ["edit-message", "delete-message", "reply-message", "react", "vote-poll"],
  data() {
    return {
      isEditing: false,
      editText: "",
      showActions: false,
      showPalette: false,
      paletteEmojis: ["👍", "❤️", "😂", "😮", "😢", "👏"],
    };
  },
  computed: {
    isOwnMessage() {
      return this.message.userId == this.currentUserId;
    },
    // Recipients (not the sender) who have read this message.
    readCount() {
      if (!Array.isArray(this.message.readById)) return 0;
      const senderId = String(this.message.userId);
      return [...new Set(this.message.readById.map(String))].filter(
        (id) => id !== senderId,
      ).length;
    },
    allOthersRead() {
      return this.groupSize > 1 && this.readCount >= this.groupSize - 1;
    },
    readReceiptText() {
      if (this.readCount === 0) return "Delivered";
      if (this.allOthersRead) return "Read by everyone";
      return `Read by ${this.readCount}`;
    },
    reactionEntries() {
      const reactions = this.message.reactions || {};
      return Object.entries(reactions)
        .filter(([, uids]) => Array.isArray(uids) && uids.length > 0)
        .map(([emoji, uids]) => [emoji, uids.map(String)]);
    },
    isPoll() {
      return (
        this.message.messageType === "poll" &&
        this.message.poll &&
        Array.isArray(this.message.poll.options)
      );
    },
    // Vote tally per option index (parallel to poll.options).
    pollVoteCounts() {
      const poll = this.message.poll || {};
      const votes = poll.votes || {};
      const counts = (poll.options || []).map(() => 0);
      Object.values(votes).forEach((arr) => {
        (Array.isArray(arr) ? arr : []).forEach((idx) => {
          if (idx >= 0 && idx < counts.length) counts[idx] += 1;
        });
      });
      return counts;
    },
    pollTotalVotes() {
      return this.pollVoteCounts.reduce((a, b) => a + b, 0);
    },
    // Option indices the current user has voted for.
    myPollVotes() {
      const votes = (this.message.poll || {}).votes || {};
      const mine = votes[String(this.currentUserId)];
      return Array.isArray(mine) ? mine : [];
    },
    canEdit() {
      return this.allowEditing && this.isOwnMessage && this.isRecentMessage();
    },
    canDelete() {
      // Admin and Coach can delete any message
      if (
        this.userData.userType === "Admin" ||
        this.userData.userType === "Coach"
      ) {
        return this.allowEditing;
      }
      // Regular users can only delete their own recent messages
      return this.allowEditing && this.isOwnMessage && this.isRecentMessage();
    },
  },
  methods: {
    getImgUrl(pic) {
      return pic !== undefined && pic !== "" ? `/images/${pic}` : "";
    },
    getTimeFromDate(date) {
      return moment(date).format("dddd, DD-MMM-YYYY HH.mm");
    },
    isRecentMessage() {
      // Allow editing for messages less than 15 minutes old
      const messageTime = new Date(this.message.createdDate);
      const now = new Date();
      const diffMinutes = (now - messageTime) / (1000 * 60);
      return diffMinutes < 15;
    },
    startEdit() {
      this.editText = this.message.messageContent;
      this.isEditing = true;
      this.$nextTick(() => {
        if (this.$refs.editTextarea) {
          this.$refs.editTextarea.focus();
          this.$refs.editTextarea.select();
        }
      });
    },
    cancelEdit() {
      this.isEditing = false;
      this.editText = "";
    },
    saveEdit() {
      if (!this.editText.trim()) {
        this.cancelEdit();
        return;
      }

      this.$emit("edit-message", {
        messageId: this.message.id,
        newContent: this.editText.trim(),
      });
      this.isEditing = false;
      this.editText = "";
    },
    toggleReaction(emoji) {
      const uids = (this.message.reactions || {})[emoji] || [];
      this.$emit("react", {
        messageId: this.message.id,
        emoji,
        hasReacted: uids.map(String).includes(String(this.currentUserId)),
      });
    },
    pickReaction(emoji) {
      this.showPalette = false;
      this.toggleReaction(emoji);
    },
    pollPercent(index) {
      if (this.pollTotalVotes === 0) return 0;
      return Math.round(
        (this.pollVoteCounts[index] / this.pollTotalVotes) * 100,
      );
    },
    votePoll(optionIndex) {
      this.$emit("vote-poll", {
        messageId: this.message.id,
        optionIndex,
        allowMultiple: !!(this.message.poll || {}).allowMultiple,
        myVotes: this.myPollVotes,
      });
    },
    replyToMessage() {
      const preview = this.message.messageContent
        ? this.message.messageContent.slice(0, 90)
        : this.message.media
          ? "📎 Attachment"
          : "";
      this.$emit("reply-message", {
        id: this.message.id,
        name: this.message.name || "User",
        preview,
      });
    },
    deleteMessage() {
      const isAdminOrCoach =
        this.userData.userType === "Admin" ||
        this.userData.userType === "Coach";
      const deleteMessage = isAdminOrCoach
        ? "Are you sure you want to delete this message? (Admin/Coach privilege)"
        : "Are you sure you want to delete this message?";

      if (confirm(deleteMessage)) {
        this.$emit("delete-message", this.message.id);
      }
    },

    // Media handling methods
    getMediaType(media) {
      if (!media) return null;
      try {
        const mediaData = typeof media === "string" ? JSON.parse(media) : media;
        return mediaData.category || "document";
      } catch (error) {
        console.error("Error parsing media data:", error);
        return "document";
      }
    },

    getMediaUrl(media) {
      if (!media) return "";
      try {
        const mediaData = typeof media === "string" ? JSON.parse(media) : media;
        const url = mediaData.url || "";
        // Root-relative so media resolves regardless of the current route
        // (a bare "chat-media/…" 404s from nested paths like /chats/…).
        if (url && !/^(https?:)?\/\//.test(url) && !url.startsWith("/")) {
          return "/" + url;
        }
        return url;
      } catch (error) {
        console.error("Error parsing media data:", error);
        return "";
      }
    },

    getFileNameFromMedia(media) {
      if (!media) return "";
      try {
        const mediaData = typeof media === "string" ? JSON.parse(media) : media;
        return mediaData.originalName || mediaData.filename || "Unknown file";
      } catch (error) {
        console.error("Error parsing media data:", error);
        return "Unknown file";
      }
    },

    getFileIconFromMedia(media) {
      if (!media) return "📄";
      try {
        const mediaData = typeof media === "string" ? JSON.parse(media) : media;
        const filename = mediaData.originalName || mediaData.filename || "";
        const extension = filename.split(".").pop().toLowerCase();

        const icons = {
          pdf: "📄",
          doc: "📄",
          docx: "📄",
          txt: "📄",
          xls: "📊",
          xlsx: "📊",
          ppt: "📊",
          pptx: "📊",
          zip: "📁",
          rar: "📁",
          "7z": "📁",
        };
        return icons[extension] || "📄";
      } catch (error) {
        console.error("Error parsing media data:", error);
        return "📄";
      }
    },

    openMediaModal(media) {
      if (!media) return;
      try {
        const mediaData = typeof media === "string" ? JSON.parse(media) : media;
        if (mediaData.category === "image") {
          // Open image in new tab/window
          window.open(mediaData.url, "_blank");
        }
      } catch (error) {
        console.error("Error opening media modal:", error);
      }
    },

    formatMessageContent(message) {
      // Process the message for special formatting (squad announcements, etc.)
      return processMessage(message.messageContent || "", {
        squadStyle: "badges", // Can be 'badges', 'list', or 'grid'
      });
    },

    isSquadAnnouncement(message) {
      return isSquadMessage(message.messageContent || "");
    },
  },
};
</script>

<style scoped lang="scss">
.read-receipt {
  display: inline-flex;
  align-items: center;
  gap: 2px;

  .read-count {
    font-size: 0.7rem;
    font-weight: 700;
    color: #64748b;
  }
}

.poll-message {
  min-width: 15rem;
  max-width: 22rem;

  .poll-question {
    font-weight: 600;
    color: #13519d;
    margin-bottom: 0.6rem;
    word-wrap: break-word;

    i {
      color: #2a8ce2;
      margin-right: 0.25rem;
    }
  }

  .poll-options {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .poll-option {
    position: relative;
    display: block;
    width: 100%;
    text-align: left;
    border: 1px solid #cfe0f2;
    background: #fff;
    border-radius: 8px;
    padding: 0;
    overflow: hidden;
    cursor: pointer;

    &:hover {
      border-color: #2a8ce2;
    }

    &.voted {
      border-color: #2a8ce2;
    }

    .poll-option-fill {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      background: rgba(42, 140, 226, 0.14);
      transition: width 0.3s ease;
      z-index: 0;
    }

    &.voted .poll-option-fill {
      background: rgba(42, 140, 226, 0.28);
    }

    .poll-option-body {
      position: relative;
      z-index: 1;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.45rem 0.6rem;
    }

    .poll-option-check {
      flex-shrink: 0;
      color: #2a8ce2;
      font-size: 0.95rem;
      display: inline-flex;
    }

    .poll-option-label {
      flex: 1;
      color: #1f2937;
      font-size: 0.9rem;
      word-wrap: break-word;
    }

    .poll-option-count {
      flex-shrink: 0;
      font-weight: 700;
      font-size: 0.8rem;
      color: #64748b;
    }
  }

  .poll-footer {
    margin-top: 0.5rem;
    font-size: 0.75rem;
    color: #64748b;
  }
}

.reaction-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;

  .reaction-chip {
    border: 1px solid #e2e8f0;
    background: #fff;
    border-radius: 12px;
    padding: 1px 8px;
    font-size: 0.85rem;
    cursor: pointer;
    line-height: 1.4;

    .reaction-count {
      font-size: 0.7rem;
      font-weight: 700;
      color: #64748b;
    }

    &.mine {
      border-color: #2a8ce2;
      background: rgba(42, 140, 226, 0.1);
    }
  }
}

.reaction-palette {
  display: flex;
  gap: 2px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 2px 6px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);

  .palette-emoji {
    border: none;
    background: none;
    font-size: 1.1rem;
    cursor: pointer;
    padding: 2px 4px;
    border-radius: 8px;

    &:hover {
      background: #f1f5f9;
    }
  }
}

.reply-quote {
  display: flex;
  flex-direction: column;
  border-left: 3px solid #2a8ce2;
  background: rgba(42, 140, 226, 0.08);
  border-radius: 6px;
  padding: 6px 10px;
  margin-bottom: 6px;

  .reply-quote-name {
    font-size: 0.75rem;
    font-weight: 700;
    color: #2a8ce2;
  }

  .reply-quote-preview {
    font-size: 0.8rem;
    color: #475569;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 32rem;
  }
}

.chat-details {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;

  img {
    height: 2rem;
    border-radius: 100px;
    border: 1px solid;
  }

  .chat-wrapper {
    background: #ece9e9;
    margin: 0 1rem 1rem 0;
    padding: 1rem;
    border-radius: 10px;
    color: #13519d;
    max-width: 90%;

    .chat-name-time-wrapper {
      display: flex;
      align-items: baseline;

      span.chat-name {
        display: block;
        font-size: 0.9rem;
        color: #2a8ce2;
      }

      span.chat-time {
        font-size: 0.8rem;
      }
    }

    .chat-message-wrapper {
      position: relative;

      .message-media {
        margin-bottom: 0.5rem;

        .media-image {
          .chat-image {
            max-width: 300px;
            max-height: 200px;
            border-radius: 8px;
            cursor: pointer;
            transition: transform 0.2s ease;

            &:hover {
              transform: scale(1.05);
            }
          }
        }

        .media-video {
          .chat-video {
            max-width: 400px;
            max-height: 300px;
            border-radius: 8px;
          }
        }

        .media-audio {
          .chat-audio {
            width: 100%;
            max-width: 300px;
          }
        }

        .media-file {
          .file-download {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 10px;
            background-color: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            max-width: 300px;

            .file-icon {
              font-size: 24px;
              width: 40px;
              height: 40px;
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .file-details {
              flex: 1;
              overflow: hidden;

              .file-name {
                display: block;
                font-weight: 500;
                font-size: 14px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                margin-bottom: 4px;
              }

              .download-link {
                color: #007bff;
                text-decoration: none;
                font-size: 12px;

                &:hover {
                  text-decoration: underline;
                }
              }
            }
          }
        }
      }

      .chat-message {
        display: block;
        word-wrap: break-word;
        // Keep the author's line breaks (numbered squads, team lists).
        white-space: pre-wrap;

        &.squad-message {
          .squad-announcement {
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            padding: 12px;
            margin: 4px 0;

            .squad-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 8px;
              padding-bottom: 6px;
              border-bottom: 1px solid #dee2e6;

              .squad-title {
                font-weight: bold;
                color: #2a8ce2;
                text-transform: uppercase;
                font-size: 0.9rem;
              }

              .squad-count {
                color: #6c757d;
                font-size: 0.8rem;
                font-style: italic;
              }
            }

            .squad-players-badges {
              display: flex;
              flex-wrap: wrap;
              gap: 6px;

              .squad-player-badge {
                background: #2a8ce2;
                color: white;
                padding: 4px 8px;
                border-radius: 12px;
                font-size: 0.8rem;
                font-weight: 500;
                white-space: nowrap;
              }
            }

            .squad-players-list {
              margin: 0;
              padding-left: 20px;

              .squad-player-item {
                margin-bottom: 4px;
                font-size: 0.9rem;
              }
            }

            .squad-players-grid {
              .squad-grid-row {
                display: flex;
                gap: 8px;
                margin-bottom: 6px;

                .squad-grid-cell {
                  flex: 1;
                  background: #e9ecef;
                  padding: 6px 8px;
                  border-radius: 4px;
                  font-size: 0.85rem;
                  text-align: center;
                  min-height: 28px;
                  display: flex;
                  align-items: center;
                  justify-content: center;

                  &.squad-grid-empty {
                    background: transparent;
                  }
                }
              }
            }
          }
        }
      }

      .edit-message-form {
        .edit-textarea {
          width: 100%;
          border: 1px solid #ccc;
          border-radius: 4px;
          padding: 0.5rem;
          font-family: inherit;
          font-size: inherit;
          resize: vertical;
          min-height: 2.5rem;
          background: white;
        }

        .edit-actions {
          display: flex;
          gap: 0.5rem;
          margin-top: 0.5rem;

          button {
            padding: 0.25rem 0.75rem;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 0.875rem;

            &.save-btn {
              background: #28a745;
              color: white;

              &:hover:not(:disabled) {
                background: #218838;
              }

              &:disabled {
                background: #6c757d;
                cursor: not-allowed;
              }
            }

            &.cancel-btn {
              background: #6c757d;
              color: white;

              &:hover {
                background: #5a6268;
              }
            }
          }
        }
      }

      .message-actions {
        position: absolute;
        top: -10px;
        right: -10px;
        display: flex;
        background: white;
        border: 1px solid #dee2e6;
        border-radius: 4px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

        .action-btn {
          background: none;
          border: none;
          padding: 0.25rem 0.5rem;
          cursor: pointer;
          color: #6c757d;
          font-size: 0.875rem;

          &:hover {
            color: #495057;
            background: #f8f9fa;
          }

          &.delete-btn:hover {
            color: #dc3545;
          }
        }
      }
    }

    .edited-indicator {
      font-style: italic;
      color: #6c757d;
      font-size: 0.7rem;
    }
  }
}

.chat-details.receiver {
  justify-content: flex-end;

  .chat-wrapper {
    background: #e1f5d7;
  }
}
</style>
